/**
 * Convierte el instrumento Fase 2 (banco vigente del cargo) en filas
 * compatibles con PerformanceEvaluation / CompetenciasTable.
 *
 * Una fila = un comportamiento habilitado para la fuente indicada.
 * id = id_comportamiento (se persiste como id_aspecto en save-native).
 */

export function mapInstrumentoToRows(instrumento, fuente = "auto") {
  if (!instrumento || !Array.isArray(instrumento.competencias)) {
    return [];
  }

  const rows = [];
  for (const competencia of instrumento.competencias) {
    const comportamientos = competencia.comportamientos || [];
    for (const comportamiento of comportamientos) {
      const fuentes = comportamiento.fuentes || {};
      if (!fuentes[fuente]) continue;

      rows.push({
        id: Number(comportamiento.id_comportamiento),
        aspecto: comportamiento.descripcion || "",
        apartado: competencia.nombre || "",
        definicion: competencia.definicion || "",
        worker: "",
        boss: "",
        average: "",
        justificacionTrabajador: "",
        justificacionJefe: "",
      });
    }
  }
  return rows;
}

/** Fusiona calificaciones guardadas (por id) sobre el catálogo actual. */
export function mergeCalificacionesEnRows(catalogRows, savedRowsOrCompetencias) {
  if (!Array.isArray(catalogRows) || catalogRows.length === 0) {
    return catalogRows || [];
  }
  if (!Array.isArray(savedRowsOrCompetencias) || savedRowsOrCompetencias.length === 0) {
    return catalogRows;
  }

  return catalogRows.map((r) => {
    const found = savedRowsOrCompetencias.find((c) => {
      const cid = c.id ?? c.id_aspecto;
      return Number(cid) === Number(r.id);
    });
    if (!found) return r;

    // Forma localStorage (rows) o API (evaluacion_competencias)
    const worker =
      found.worker !== undefined && found.worker !== ""
        ? found.worker
        : found.calificacion_empleado
          ? Number(found.calificacion_empleado)
          : r.worker;
    const boss =
      found.boss !== undefined && found.boss !== ""
        ? found.boss
        : found.calificacion_jefe
          ? Number(found.calificacion_jefe)
          : r.boss;

    return {
      ...r,
      worker: worker === 0 ? "" : worker,
      boss: boss === 0 ? "" : boss,
      average:
        found.average !== undefined && found.average !== ""
          ? String(found.average)
          : found.promedio
            ? String(found.promedio)
            : r.average,
      justificacionTrabajador:
        found.justificacionTrabajador || found.justificacion_empleado || r.justificacionTrabajador,
      justificacionJefe:
        found.justificacionJefe || found.justificacion_jefe || r.justificacionJefe,
    };
  });
}

/** Promedios por nombre de competencia/apartado (dinámico). */
export function calcularPromediosPorApartadoDinamico(rows) {
  const grupos = {};
  for (const row of rows || []) {
    const nombre = row.apartado || "Sin grupo";
    if (!grupos[nombre]) grupos[nombre] = [];
    grupos[nombre].push(row);
  }

  const promedios = {};
  Object.entries(grupos).forEach(([nombre, items]) => {
    let suma = 0;
    let cuenta = 0;
    items.forEach((row) => {
      const workerVal = Number(row.worker) || 0;
      const bossVal = Number(row.boss) || 0;
      if (workerVal > 0 && bossVal > 0) {
        suma += (workerVal + bossVal) / 2;
        cuenta += 1;
      } else if (workerVal > 0) {
        suma += workerVal;
        cuenta += 1;
      } else if (bossVal > 0) {
        suma += bossVal;
        cuenta += 1;
      }
    });
    promedios[nombre] = cuenta === 0 ? 0 : Number((suma / cuenta).toFixed(2));
  });
  return promedios;
}

/** Agrupa filas por apartado preservando orden. */
export function agruparRowsPorApartado(rows) {
  const orden = [];
  const mapa = new Map();
  for (const row of rows || []) {
    const key = row.apartado || "Sin grupo";
    if (!mapa.has(key)) {
      mapa.set(key, {
        apartado: key,
        definicion: row.definicion || "",
        items: [],
      });
      orden.push(key);
    }
    mapa.get(key).items.push(row);
  }
  return orden.map((k) => mapa.get(k));
}

/** Calificaciones 1, 2 o 5 requieren justificación escrita. */
export function requiereJustificacionCalificacion(valor) {
  const n = Number(valor);
  return n === 5 || (n > 0 && n <= 2);
}

/** Mínimo de caracteres para justificar calificaciones 1, 2 o 5. */
export const MIN_CARACTERES_JUSTIFICACION = 30;

export function justificacionCumpleMinimo(texto) {
  return String(texto || "").trim().length >= MIN_CARACTERES_JUSTIFICACION;
}

/**
 * Bloquea una fila si alguna anterior (en orden visual) tiene 1, 2 o 5
 * sin justificación válida. role: 'worker' | 'boss'
 */
export function filaBloqueadaPorJustificacionPendiente(orderedRows, index, role = "worker") {
  if (!Array.isArray(orderedRows) || index <= 0) return false;

  for (let i = 0; i < index; i++) {
    const prev = orderedRows[i];
    if (!prev) continue;
    const valor = role === "boss" ? prev.boss : prev.worker;
    const justif =
      role === "boss" ? prev.justificacionJefe : prev.justificacionTrabajador;
    if (requiereJustificacionCalificacion(valor) && !justificacionCumpleMinimo(justif)) {
      return true;
    }
  }
  return false;
}

/** Lista plana de filas en el mismo orden visual que la tabla agrupada. */
export function aplanarRowsEnOrdenVisual(rows) {
  return agruparRowsPorApartado(rows || []).flatMap((g) => g.items || []);
}

function recalcularAverage(row) {
  const workerVal = Number(row.worker) || 0;
  const bossVal = Number(row.boss) || 0;
  if (workerVal === 0 && bossVal === 0) return "";
  return ((workerVal + bossVal) / 2).toFixed(1);
}

/** Vacía calificaciones y justificaciones de todas las filas posteriores (orden visual). */
export function limpiarCalificacionesPosteriores(rows, fromRowId, role = "worker") {
  const orden = aplanarRowsEnOrdenVisual(rows);
  const idx = orden.findIndex((r) => Number(r.id) === Number(fromRowId));
  if (idx < 0) return rows;

  const idsALimpiar = new Set(orden.slice(idx + 1).map((r) => r.id));

  return (rows || []).map((row) => {
    if (!idsALimpiar.has(row.id)) return row;
    const cleared =
      role === "boss"
        ? { ...row, boss: "", justificacionJefe: "" }
        : { ...row, worker: "", justificacionTrabajador: "" };
    return { ...cleared, average: recalcularAverage(cleared) };
  });
}

/**
 * Actualiza una calificación; si es 1, 2 o 5, limpia las posteriores
 * hasta que se complete la justificación mínima.
 */
export function aplicarCambioCalificacionSecuencial(rows, rowId, field, numericValue) {
  const role = field === "boss" ? "boss" : "worker";
  let updated = (rows || []).map((row) => {
    if (Number(row.id) !== Number(rowId)) return row;
    const newRow = { ...row, [field]: numericValue === 0 ? "" : numericValue };
    // Si ya no requiere justificación, limpiar el texto residual
    if (!requiereJustificacionCalificacion(newRow[field])) {
      if (role === "boss") newRow.justificacionJefe = "";
      else newRow.justificacionTrabajador = "";
    }
    newRow.average = recalcularAverage(newRow);
    return newRow;
  });

  if (requiereJustificacionCalificacion(numericValue)) {
    updated = limpiarCalificacionesPosteriores(updated, rowId, role);
  }
  return updated;
}

/**
 * Actualiza justificación; si queda por debajo del mínimo, limpia posteriores.
 */
export function aplicarCambioJustificacionSecuencial(rows, rowId, role, texto) {
  const justifKey = role === "boss" ? "justificacionJefe" : "justificacionTrabajador";
  const valorKey = role === "boss" ? "boss" : "worker";

  let updated = (rows || []).map((row) =>
    Number(row.id) === Number(rowId) ? { ...row, [justifKey]: texto } : row
  );

  const actual = updated.find((r) => Number(r.id) === Number(rowId));
  if (
    actual &&
    requiereJustificacionCalificacion(actual[valorKey]) &&
    !justificacionCumpleMinimo(texto)
  ) {
    updated = limpiarCalificacionesPosteriores(updated, rowId, role);
  }
  return updated;
}
